import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { NotificationManager } from "react-notifications";
import Router from "next/router";
import { useSelector } from "react-redux";
import { useState } from "react";

import FormInput from "../formInput/formInput";
import axiosInstance from "../../config/axios";
import { loader } from "../../store/actions/loader";
import { storeAuth } from "../../store/actions/auth";
import InlineLoading from "../../components/UI/inlineLoader";
import FacebookLoginComponent from "../FacebookLoginComponent";
import GoogleLoginComponent from "../GoogleLoginComponent";

const Signup = () => {
  const [inlineLoader, setInlineLoader] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);
  const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
  const [inputType, setInputType] = useState("text");

  const loadingState = useSelector((state) => state.loader.loading);

  const { register, handleSubmit, errors, reset, watch } = useForm({
    mode: "onChange",
  });

  // watch the password field so we can compare it in confirm password validation
  const passwordValue = watch("password", "");
  const dispatch = useDispatch();

  const handleFocus = () => {
    setInputType("date");
  };

  const onFacebookLogin = async (data) => {
    if (data) {
      const userData = {
        first_name: data.name.split(" ")[0],
        last_name: data.name.split(" ")[1] ? data.name.split(" ")[1] : "",
        email: data.email,
        social_media_type: data.graphDomain,
        signup_device: "web",
      };
      try {
        const {
          data: { data: response },
        } = await axiosInstance.post("social-login", userData);
        const user = { token: response.token, user: response.user };
        dispatch(storeAuth(user));
        NotificationManager.success("Login successful", "", 3000);
        if (!response.user.phone) {
          Router.push("/");
        } else {
          let redirectTo = localStorage.getItem("checkoutToLogin");
          if (redirectTo) {
            Router.push(redirectTo);
            localStorage.removeItem("checkoutToLogin");
          } else {
            Router.push("/");
          }
        }
      } catch (e) {}
    } else {
      return;
    }
  };

  const googleLoginHandler = async (data) => {
    if (data) {
      const userData = {
        first_name: data.profileObj.givenName,
        last_name: data.profileObj.familyName ? data.profileObj.familyName : "",
        email: data.profileObj.email,
        social_media_type: "google",
        signup_device: "web",
        id_token: data.tokenId,
      };
      try {
        const {
          data: { data: response },
        } = await axiosInstance.post("V1/social-login", userData);
        const user = { token: response.token, user: response.user };
        dispatch(storeAuth(user));
        NotificationManager.success("Login successful", "", 3000);
        if (!response.user.phone) {
          Router.push("/");
        } else {
          let redirectTo = localStorage.getItem("checkoutToLogin");
          if (redirectTo) {
            Router.push(redirectTo);
            localStorage.removeItem("checkoutToLogin");
          } else {
            Router.push("/");
          }
        }
      } catch (e) {
          NotificationManager.error("Signup failed", "", 3000);
      }
    } else {
      return;
    }
  };

  const signupHandler = async (data) => {
    let p = data.phone.substring(1, data.phone.length);
    let number = "234";
    data["phone"] = number + p;

    // remove confirm_password before sending to API
    delete data.confirm_password;

    dispatch(loader());
    setInlineLoader(true);
    try {
      if (data) {
        const { data: response } = await axiosInstance.post("register", {
          ...data,
          signup_device: "web",
        });
        dispatch(loader());
        setInlineLoader(false);
        const res = data;
        localStorage.setItem("signupToVerification", JSON.stringify(res));
        Router.push("/otp-verification");
      }
      NotificationManager.success("Account Registration Successful", "", 5000);
    } catch (error) {
      dispatch(loader());
      setInlineLoader(false);
      NotificationManager.error(error.response.data.message, "", 5000);
    }
    reset({});
  };

  const verifyEmailHandler = async (email) => {
    try {
      const {
        data: {
          data: { email_exists },
        },
      } = await axiosInstance.post("verify-email", { email });

      if (email_exists) {
        return "Email already exists. Do you want to login instead?";
      }

      return true;
    } catch (error) {
      return "Unable to verify email. Please try again.";
    }
  };

  const verifyPhoneHandler = async (phone) => {
    try {
      const {
        data: {
          data: { phone_exists },
        },
      } = await axiosInstance.post("verify-phone", { phone });
      return (
        !phone_exists ||
        "Phone number already exists. Kindly use another number"
      );
    } catch (error) {}
  };

  const togglePasswordVisiblity = () => {
    setPasswordShown((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordShown((prev) => !prev);
  };

  return (
    <>
      <div className="col-md-5 mt-md-0 mt-5 mb-md-0 mb-5">
        <h3>Create New Account</h3>
        <p className="mb-3">Create your very own Kilimanjaro Account</p>
        <form onSubmit={handleSubmit(signupHandler)} className="signup-form">
          <FormInput
            type="text"
            name="first_name"
            placeholder="First Name*"
            register={register({ required: "First name is required" })}
            error={errors.first_name && errors.first_name.message}
          />
          <FormInput
            type="text"
            name="last_name"
            placeholder="Last Name*"
            register={register({ required: "Last name is required" })}
            error={errors.last_name && errors.last_name.message}
          />
          <FormInput
            type="number"
            name="phone"
            placeholder="Phone number e.g. 07052262158*"
            register={register({
              required: true,
              validate: async (value) => verifyPhoneHandler(value),
            })}
            error={errors.phone && errors.phone.message}
          />
          <FormInput
            type="email"
            name="email"
            placeholder="example@email.com*"
            register={register({
              required: "Please input a valid email address",
              validate: async (value) => verifyEmailHandler(value),
            })}
            error={errors.email && errors.email.message}
          />
          <FormInput
            type={inputType}
            name="birth_date"
            placeholder="Date of birth"
            onFocus={handleFocus}
            register={register({})}
            error={errors.dob && errors.dob.message}
          />

          {/* ── Password ─────────────────────────────────────────────── */}
          <div className="mt-3">
            <div className="textbox">
              <input
                type={passwordShown ? "text" : "password"}
                name="password"
                placeholder="Password*"
                ref={register({
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                })}
              />
              <i
                onClick={togglePasswordVisiblity}
                className={passwordShown ? "fa fa-eye" : "fa fa-eye-slash"}
                aria-hidden="true"
              />
              <div
                className={`border ${errors.password ? "border-error" : null}`}
              />
            </div>
            {errors.password && (
              <p className="error">{errors.password.message}</p>
            )}
          </div>

          {/* ── Confirm Password ─────────────────────────────────────── */}
          <div className="mt-3">
            <div className="textbox">
              <input
                type={confirmPasswordShown ? "text" : "password"}
                name="confirm_password"
                placeholder="Confirm Password*"
                ref={register({
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === watch("password") || "Passwords do not match",
                })}
              />
              <i
                onClick={toggleConfirmPasswordVisibility}
                className={
                  confirmPasswordShown ? "fa fa-eye" : "fa fa-eye-slash"
                }
                aria-hidden="true"
              />
              <div
                className={`border ${
                  errors.confirm_password ? "border-error" : null
                }`}
              />
            </div>
            {errors.confirm_password && (
              <p className="error">{errors.confirm_password.message}</p>
            )}
          </div>

          <div className="checkbox-wrapper mt-3">
            <input
              className="mt-2"
              type="checkbox"
              id="check"
              name="check"
              value="checked"
            />
            <label className="mt-0 ml-3" htmlFor="check">
              I authorize Kilimanjaro to send notifications on
              SMS/Messages/Promotional/Informational messages
            </label>
          </div>

          {loadingState && inlineLoader ? (
            <InlineLoading />
          ) : (
            <button className="btn btn-login mt-3">
              <span className="text">Register</span>
            </button>
          )}
        </form>

        <p className="mt-3">Or sign up with</p>
        <div className="other-signin-option">
          <div className="gle-btn mt-2">
            <GoogleLoginComponent onLogin={googleLoginHandler} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Signup;
